import { Link } from "react-router-dom";
import { ArrowLeft, BookMarked } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  PHONETIC_SYMBOLS,
  DIGRAPHS,
  SYLLABLE_PATTERNS,
  PUNCTUATION,
  GAME_ICON_KEY,
  LESSON_EMOJI_KEY,
  APP_NOTATION,
} from "@/lib/symbolsData";

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} data-testid={`symbols-${id}`} className="mt-14">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
        {title}
      </h2>
      {subtitle && <p className="mt-2 text-slate-600 max-w-3xl">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Row({ symbol, meaning, example }) {
  return (
    <div className="grid grid-cols-[minmax(80px,120px)_1fr] sm:grid-cols-[minmax(120px,160px)_1fr_1fr] gap-4 items-baseline py-3 border-b border-slate-100 last:border-b-0">
      <div className="font-display text-lg sm:text-xl font-semibold text-sky-600 leading-tight break-words">
        {symbol}
      </div>
      <div className="text-slate-800 font-medium">{meaning}</div>
      {example && (
        <div className="text-slate-500 text-sm sm:text-base italic sm:text-right">
          {example}
        </div>
      )}
    </div>
  );
}

export default function Symbols() {
  return (
    <div className="min-h-screen relative">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <Link
          to="/"
          data-testid="symbols-back-home"
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-sky-50 text-sky-700 px-3 py-1 rounded-full mb-3">
          <BookMarked className="w-3.5 h-3.5" /> Reference
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
          Symbols &amp; meaning
        </h1>
        <p className="mt-4 text-slate-600 text-lg max-w-3xl">
          Every symbol, icon, emoji, and notation you&rsquo;ll come across in
          PhonX &mdash; explained with examples. Bookmark this page and jump
          back any time.
        </p>

        {/* Quick jump */}
        <nav className="mt-8 flex flex-wrap gap-2">
          {[
            ["phonetic", "Phonetic sounds"],
            ["digraphs", "Digraphs & trigraphs"],
            ["syllable", "Syllable patterns"],
            ["punctuation", "Punctuation"],
            ["notation", "App notation"],
            ["icons", "Game icons"],
            ["emojis", "Lesson emojis"],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              data-testid={`symbols-jump-${id}`}
              className="rounded-full px-4 py-2 text-sm font-bold bg-white border-2 border-slate-200 text-slate-700 hover:border-sky-400 hover:text-sky-600 transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        <Section
          id="phonetic"
          title="Phonetic sounds"
          subtitle="Slashes /like this/ wrap a phonic sound — the way a letter (or letter-group) is pronounced when reading. Not the letter's name."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm">
            {PHONETIC_SYMBOLS.map((r) => (
              <Row key={r.symbol} {...r} />
            ))}
          </div>
        </Section>

        <Section
          id="digraphs"
          title="Digraphs & trigraphs"
          subtitle="Two or three letters that make one sound. Core to reading beyond the alphabet."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm">
            {DIGRAPHS.map((r) => (
              <Row key={r.pattern} symbol={r.pattern} meaning={r.note} example={r.example} />
            ))}
          </div>
        </Section>

        <Section
          id="syllable"
          title="Syllable & word patterns"
          subtitle="How words are structured — helpful for decoding longer words."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm">
            {SYLLABLE_PATTERNS.map((r) => (
              <Row key={r.pattern} symbol={r.pattern} meaning={r.note} example={r.example} />
            ))}
          </div>
        </Section>

        <Section
          id="punctuation"
          title="Punctuation marks"
          subtitle="The little marks that shape how a sentence sounds — pauses, questions, feelings, and grouping."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm">
            {PUNCTUATION.map((r) => (
              <div
                key={r.name}
                data-testid={`punct-row-${r.name.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="grid grid-cols-[minmax(70px,90px)_1fr] sm:grid-cols-[80px_180px_1fr_1fr] gap-4 items-baseline py-3 border-b border-slate-100 last:border-b-0"
              >
                <div className="font-display text-3xl sm:text-4xl font-semibold text-sky-600 leading-none">
                  {r.symbol}
                </div>
                <div className="text-slate-800 font-bold">{r.name}</div>
                <div className="col-span-2 sm:col-span-1 text-slate-700">
                  {r.meaning}
                </div>
                <div className="col-span-2 sm:col-span-1 text-slate-500 text-sm sm:text-base italic sm:text-right">
                  {r.example}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section
          id="notation"
          title="App notation"
          subtitle="Extra visual cues used across the games and lessons."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm">
            {APP_NOTATION.map((r) => (
              <Row key={r.symbol} symbol={r.symbol} meaning={r.meaning} />
            ))}
          </div>
        </Section>

        <Section
          id="icons"
          title="Game icons"
          subtitle="Each launcher card in the Games section uses a small icon representing the activity."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm grid sm:grid-cols-2 gap-y-2 gap-x-6">
            {GAME_ICON_KEY.map((r) => (
              <div
                key={r.meaning}
                className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-b-0 sm:border-b-0"
              >
                <span className="text-lg font-medium text-slate-700 min-w-[130px]">{r.icon}</span>
                <span className="text-slate-600">{r.meaning}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section
          id="emojis"
          title="Lesson emojis"
          subtitle="Every picture on a lesson card, its word, and the letter or pattern it teaches."
        >
          <div className="bg-white rounded-3xl p-5 sm:p-7 border-2 border-slate-100 shadow-sm grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
            {LESSON_EMOJI_KEY.map((r) => (
              <div
                key={r.meaning}
                className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-b-0 sm:border-b-0"
              >
                <span className="text-3xl w-10 text-center">{r.emoji}</span>
                <span className="text-slate-600">{r.meaning}</span>
              </div>
            ))}
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
